import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { Download, Trash2, ShieldCheck, FileText, KeyRound } from "lucide-react";
import PushSettings from "../components/PushSettings";

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delPw, setDelPw] = useState("");
  const [delConfirm, setDelConfirm] = useState("");
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");

  const onChangePassword = async () => {
    if (pwNew.length < 8) {
      return toast.error("Nytt passord må være minst 8 tegn");
    }
    if (pwNew !== pwConfirm) {
      return toast.error("De to nye passordene er ikke like");
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwCurrent,
        new_password: pwNew,
      });
      toast.success("Passord oppdatert");
      setPwOpen(false);
      setPwCurrent("");
      setPwNew("");
      setPwConfirm("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onExport = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/me/data-export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `canal-boats-mine-data-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Data eksportert");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setBusy(true);
    try {
      await api.delete("/me/account", { data: { password: delPw, confirm: delConfirm } });
      toast.success("Kontoen er slettet");
      setUser(false);
      navigate("/login");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <div className="text-xs uppercase tracking-[0.32em] text-[#A57F39]">Personvern</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0C1824] mt-1">Min profil</h1>
      </div>

      <Card className="p-5">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#A57F39]">Konto</div>
        <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-[#2B3F4D]">Navn</div>
            <div className="font-medium text-[#0C1824]">{user?.name}</div>
          </div>
          <div>
            <div className="text-[#2B3F4D]">E-post</div>
            <div className="font-medium text-[#0C1824]">{user?.email}</div>
          </div>
          <div>
            <div className="text-[#2B3F4D]">Rolle</div>
            <div className="font-medium text-[#0C1824] capitalize">{user?.role}</div>
          </div>
          <div>
            <div className="text-[#2B3F4D]">Opprettet</div>
            <div className="font-medium text-[#0C1824]">
              {user?.created_at && new Date(user.created_at).toLocaleDateString("nb-NO")}
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex items-start gap-3">
          <KeyRound className="w-5 h-5 text-[#BD984D] mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-[#0C1824]">Bytt passord</div>
            <p className="text-sm text-[#2B3F4D] mt-1">
              Oppdater passordet ditt. Du må kunne det nåværende for å bekrefte.
            </p>
            <Button
              onClick={() => setPwOpen(true)}
              data-testid="profile-change-password-open"
              className="mt-3 bg-[#BD984D] hover:bg-[#A57F39] text-white"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              Bytt passord
            </Button>
          </div>
        </div>
      </Card>

      <PushSettings />

      <Card className="p-5">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-[#A57F39] mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-[#0C1824]">Personvernerklæring</div>
            <p className="text-sm text-[#2B3F4D] mt-1">
              Les hvordan vi behandler dine data, hvor de lagres, og dine rettigheter.
            </p>
            <Button
              variant="outline"
              onClick={() => navigate("/personvern")}
              data-testid="profile-privacy-link"
              className="mt-3 border-[#0C1824] text-[#0C1824]"
            >
              Åpne erklæring
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex items-start gap-3">
          <Download className="w-5 h-5 text-[#1DA3B3] mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-[#0C1824]">Eksporter mine data</div>
            <p className="text-sm text-[#2B3F4D] mt-1">
              Last ned alt vi har lagret om deg som en JSON-fil (GDPR art. 20).
            </p>
            <Button
              onClick={onExport}
              disabled={busy}
              data-testid="profile-export"
              className="mt-3 bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              {busy ? "Laster ned…" : "Last ned (.json)"}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-5 border-red-200">
        <div className="flex items-start gap-3">
          <Trash2 className="w-5 h-5 text-red-600 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-[#0C1824]">Slett min konto</div>
            <p className="text-sm text-[#2B3F4D] mt-1">
              Permanent sletting av kontoen din (GDPR art. 17). Audit-logger for sikkerhet/
              forsikring beholdes, men anonymisert. Handlingen kan ikke angres.
            </p>
            <Button
              variant="outline"
              onClick={() => setDelOpen(true)}
              data-testid="profile-delete-open"
              className="mt-3 border-red-300 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Slett kontoen min
            </Button>
          </div>
        </div>
      </Card>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bytt passord</DialogTitle>
            <DialogDescription>
              Skriv inn nåværende passord og ditt nye passord (minst 8 tegn).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nåværende passord</Label>
              <Input
                type="password"
                autoComplete="current-password"
                value={pwCurrent}
                onChange={(e) => setPwCurrent(e.target.value)}
                data-testid="profile-pw-current"
              />
            </div>
            <div>
              <Label>Nytt passord</Label>
              <Input
                type="password"
                autoComplete="new-password"
                value={pwNew}
                onChange={(e) => setPwNew(e.target.value)}
                data-testid="profile-pw-new"
              />
            </div>
            <div>
              <Label>Bekreft nytt passord</Label>
              <Input
                type="password"
                autoComplete="new-password"
                value={pwConfirm}
                onChange={(e) => setPwConfirm(e.target.value)}
                data-testid="profile-pw-confirm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)}>Avbryt</Button>
            <Button
              onClick={onChangePassword}
              disabled={busy || !pwCurrent || !pwNew || !pwConfirm}
              data-testid="profile-pw-submit"
              className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
            >
              Lagre nytt passord
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Permanent sletting</DialogTitle>
            <DialogDescription>
              For å bekrefte, skriv passordet ditt og ordet <b>SLETT</b> i feltene under.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Passord</Label>
              <Input
                type="password"
                value={delPw}
                onChange={(e) => setDelPw(e.target.value)}
                data-testid="profile-delete-password"
              />
            </div>
            <div>
              <Label>Skriv «SLETT» for å bekrefte</Label>
              <Input
                value={delConfirm}
                onChange={(e) => setDelConfirm(e.target.value)}
                placeholder="SLETT"
                data-testid="profile-delete-confirm"
              />
            </div>
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-xs text-red-800">
              <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                Etter sletting kan du ikke logge inn igjen. Kontakt admin hvis du senere
                ønsker tilgang på nytt.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOpen(false)}>Avbryt</Button>
            <Button
              onClick={onDelete}
              disabled={busy || delConfirm !== "SLETT" || !delPw}
              data-testid="profile-delete-submit"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Slett konto permanent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
